import React, { useState, useRef } from "react";
import { Modal, Input, Button, Spin, Select, Tag, message } from "antd";
import { UserOutlined, PlusOutlined, SearchOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_DOMAIN } from "../constants";

const SOURCE_OPTIONS = [
  { value: "walk_in",    label: "Walk-in" },
  { value: "phone_call", label: "Phone Call" },
  { value: "manual",     label: "Manual Entry" },
  { value: "wati",       label: "WhatsApp" },
  { value: "b2b",        label: "B2B" },
  { value: "instagram",  label: "Instagram" },
  { value: "website",    label: "Website" },
  { value: "other",      label: "Other" },
];

const TYPE_OPTIONS = [
  { value: "B2C", label: "B2C – Individual" },
  { value: "B2B", label: "B2B – Business" },
];

function mapToDrawerShape(c) {
  return {
    customer_id:           c.customer_id ?? c.id,
    customer_name:         c.customer_name ?? c.name ?? "",
    customer_real_name:    c.customer_real_name ?? c.real_name ?? "",
    customer_mobile:       c.customer_mobile ?? c.number ?? "",
    customer_email:        c.customer_email ?? c.email ?? "",
    customer_addresses_new: c.customer_addresses_new ?? c.addresses ?? [],
    source:  c.source  ?? null,
    type:    c.type    ?? null,
    status:  c.status  ?? 1,
  };
}

export function NewQueryModal({ open, onClose, onSelect }) {
  const [mode, setMode]               = useState("search");
  const [searchTerm, setSearchTerm]   = useState("");
  const [results, setResults]         = useState([]);
  const [searching, setSearching]     = useState(false);
  const [createForm, setCreateForm]   = useState({ name: "", phone: "", type: null, source: null });
  const [creating, setCreating]       = useState(false);
  const searchTimer                   = useRef(null);
  const headers = { accessToken: localStorage.getItem("accessToken") };

  function reset() {
    setMode("search");
    setSearchTerm("");
    setResults([]);
    setCreateForm({ name: "", phone: "", type: null, source: null });
    setCreating(false);
  }

  function handleClose() { reset(); onClose(); }

  function handleSearch(val) {
    setSearchTerm(val);
    clearTimeout(searchTimer.current);
    if (!val?.trim()) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.post(API_DOMAIN + "/whatsapp/get_customers", { search: val }, { headers });
        setResults(res.data.success ? (res.data.data || []) : []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 400);
  }

  function handleSelect(c) {
    const obj = mapToDrawerShape(c);
    reset(); onClose(); onSelect(obj);
  }

  async function handleCreate() {
    if (!createForm.name.trim())  { message.warning("Customer name is required"); return; }
    if (!createForm.phone.trim()) { message.warning("Phone number is required"); return; }
    setCreating(true);
    try {
      const addRes = await axios.post(
        API_DOMAIN + "/whatsapp/customer_add",
        { name: createForm.name.trim(), number: createForm.phone.trim() },
        { headers }
      );
      if (!addRes.data.success) throw new Error(addRes.data.message || "Failed to create customer");

      const profileRes = await axios.get(API_DOMAIN + "/customers/get_full_profile", {
        params: { phone: createForm.phone.trim() },
        headers,
      });
      const cust = profileRes.data.data?.[0];
      if (!cust) throw new Error("Could not load created customer");

      if (createForm.type || createForm.source) {
        await axios.post(
          API_DOMAIN + "/customers/update",
          {
            customer_id: cust.customer_id,
            ...(createForm.type   && { type:   createForm.type }),
            ...(createForm.source && { source: createForm.source }),
          },
          { headers }
        );
      }

      const obj = mapToDrawerShape({
        ...cust,
        type:   createForm.type   ?? cust.type   ?? null,
        source: createForm.source ?? cust.source ?? null,
      });
      reset(); onClose(); onSelect(obj);
    } catch (e) {
      message.error(e.message || "Failed to create customer");
    } finally {
      setCreating(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="New Query — Select Customer"
      footer={null}
      width={520}
      destroyOnClose
    >
      {mode === "search" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4 }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by name or phone…"
            value={searchTerm}
            onChange={e => handleSearch(e.target.value)}
            allowClear
            autoFocus
          />

          {searching && <div style={{ textAlign: "center" }}><Spin size="small" /></div>}

          {results.length > 0 && (
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden", maxHeight: 320, overflowY: "auto" }}>
              {results.map((c, i) => (
                <div
                  key={c.customer_id ?? c.id ?? i}
                  onClick={() => handleSelect(c)}
                  style={{
                    padding: "10px 14px",
                    borderBottom: i < results.length - 1 ? "1px solid #f0f0f0" : "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}
                >
                  <UserOutlined style={{ color: "#8c8c8c", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.customer_name ?? c.name}
                    </div>
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>{c.customer_mobile ?? c.number}</div>
                  </div>
                  {c.type && (
                    <Tag color={c.type === "B2B" ? "purple" : "blue"} style={{ fontSize: 11, margin: 0 }}>{c.type}</Tag>
                  )}
                </div>
              ))}
            </div>
          )}

          {!searching && searchTerm.trim() && results.length === 0 && (
            <div style={{ color: "#8c8c8c", fontSize: 13, textAlign: "center" }}>No customers found</div>
          )}

          <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
            <Button icon={<PlusOutlined />} onClick={() => setMode("create")} block>
              Create New Customer
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => setMode("search")}
            style={{ alignSelf: "flex-start", padding: "0 4px", color: "#8c8c8c" }}
          >
            Back to search
          </Button>

          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Customer Name *</div>
            <Input
              placeholder="Full name"
              value={createForm.name}
              onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
              autoFocus
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Phone Number *</div>
            <Input
              placeholder="e.g. 971501234567"
              value={createForm.phone}
              onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Type</div>
              <Select
                options={TYPE_OPTIONS}
                value={createForm.type}
                onChange={v => setCreateForm(f => ({ ...f, type: v }))}
                placeholder="B2C / B2B"
                allowClear
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Source</div>
              <Select
                options={SOURCE_OPTIONS}
                value={createForm.source}
                onChange={v => setCreateForm(f => ({ ...f, source: v }))}
                placeholder="How did they reach us?"
                allowClear
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <Button type="primary" loading={creating} onClick={handleCreate} block style={{ marginTop: 4 }}>
            Create Customer &amp; Continue
          </Button>
        </div>
      )}
    </Modal>
  );
}
